import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { DEFAULT_CURRENCY, QUERY_KEYS } from '@/constants';
import type { TyGiaQuyDoiNgoaiTe } from '@/types/schemas';
import { Label } from '@/components/ui/label';
import type { FormMode } from '@/types/form';
import { FormField } from '../form-field';
import { useCRUD } from '@/hooks';
import { cn } from '@/lib/utils';

interface NgoaiTeInputProps {
  formMode: FormMode;
  disabled?: boolean;
  labelClassName?: string;
  className?: string;
  classNameInput?: string;
  currencyFieldName?: string;
  exchangeRateFieldName?: string;
  autoMapExchangeRate?: boolean;
  dateFieldName?: string;
  onExchangeRateChange?: (exchangeRate: number) => void;
  onCurrencyChange?: (currencyUuid: string | null) => void;
  defaultCurrency?: string;
}

export function CurrencyInput({
  formMode,
  disabled = false,
  labelClassName = 'w-32 shrink-0',
  className = 'flex items-center',
  classNameInput = 'w-20',
  currencyFieldName = 'ma_nt',
  exchangeRateFieldName = 'ty_gia',
  autoMapExchangeRate = true,
  dateFieldName = 'ngay_ct',
  onExchangeRateChange,
  onCurrencyChange,
  defaultCurrency = DEFAULT_CURRENCY
}: NgoaiTeInputProps) {
  const { data } = useCRUD<TyGiaQuyDoiNgoaiTe, any>({ endpoint: QUERY_KEYS.TY_GIA_QUY_DOI_NGOAI_TE });

  const exchangeRates = data.map(item => ({
    value: item.ma_nt,
    label: item.ma_nt_data?.ma_nt ?? item.ma_nt
  }));

  const { setValue, watch } = useFormContext<any>();
  const selectedCurrency = watch(currencyFieldName);
  const exchangeRate = watch(exchangeRateFieldName);

  useEffect(() => {
    if (!selectedCurrency) {
      const defaultCurrencyOption = exchangeRates.find(rate => rate.label === defaultCurrency);
      const defaultCurrencyValue = defaultCurrencyOption?.value || exchangeRates[0]?.value || null;
      if (defaultCurrencyValue) setValue(currencyFieldName, defaultCurrencyValue);
    }
  }, [exchangeRates, currencyFieldName, setValue, defaultCurrency, selectedCurrency]);

  const getExchangeRateValue = () => {
    if (!autoMapExchangeRate || !selectedCurrency) return undefined;
    const rate = data.find(item => item.ma_nt === selectedCurrency)?.ty_gia;
    return rate != null ? Number(rate) : undefined;
  };

  useEffect(() => {
    const normalizedRate = getExchangeRateValue();
    if (normalizedRate != null && normalizedRate !== exchangeRate) {
      setValue(exchangeRateFieldName, normalizedRate);
      onExchangeRateChange?.(normalizedRate);
    }
  }, [selectedCurrency, onExchangeRateChange]);

  useEffect(() => {
    onCurrencyChange?.(selectedCurrency ?? null);
  }, [onCurrencyChange, selectedCurrency]);

  const isDisabled = formMode === 'view' || disabled;

  return (
    <div className={className}>
      <Label className={labelClassName}>Ngoại tệ</Label>
      <FormField
        name={currencyFieldName}
        type='select'
        disabled={isDisabled}
        options={exchangeRates as { value: string; label: string }[]}
        className='w-20'
      />
      <FormField
        name={exchangeRateFieldName}
        type='number-formatted'
        disabled={isDisabled}
        className={cn('ml-1 pb-1', classNameInput)}
      />
    </div>
  );
}
